import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PhoneNumberMapping } from '@tips-crm/shared';
import {
  createPhoneNumber,
  deletePhoneNumber,
  fetchBrands,
  fetchPhoneNumbers,
  fetchSellers,
} from '../../api/endpoints.js';
import { errorMessage } from '../../api/client.js';
import { PageHeader } from '../../components/PageHeader.js';
import { DataTable, type Column } from '../../components/DataTable.js';
import { Modal } from '../../components/Modal.js';

interface PhoneForm {
  seller_id: string;
  brand_id: string;
  phone_number_e164: string;
  connect_phone_number_id: string;
}

const EMPTY: PhoneForm = {
  seller_id: '',
  brand_id: '',
  phone_number_e164: '',
  connect_phone_number_id: '',
};

export function PhoneNumbersPage() {
  const qc = useQueryClient();
  const [sellerFilter, setSellerFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<PhoneForm>(EMPTY);

  const sellersQ = useQuery({ queryKey: ['sellers'], queryFn: fetchSellers });
  const numbersQ = useQuery({
    queryKey: ['phone-numbers', sellerFilter],
    queryFn: () => fetchPhoneNumbers(sellerFilter || undefined),
  });
  const brandsQ = useQuery({
    queryKey: ['brands', form.seller_id],
    queryFn: () => fetchBrands(form.seller_id || undefined),
    enabled: open && Boolean(form.seller_id),
  });

  const sellerName = (id: string) => sellersQ.data?.find((s) => s.id === id)?.name ?? id;

  const createMutation = useMutation({
    mutationFn: () =>
      createPhoneNumber({
        seller_id: form.seller_id,
        brand_id: form.brand_id || null,
        phone_number_e164: form.phone_number_e164,
        connect_phone_number_id: form.connect_phone_number_id || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['phone-numbers'] });
      setOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePhoneNumber(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phone-numbers'] }),
  });

  function openCreate() {
    setForm({ ...EMPTY, seller_id: sellerFilter || sellersQ.data?.[0]?.id || '' });
    setOpen(true);
  }

  const columns = useMemo<Column<PhoneNumberMapping>[]>(
    () => [
      { key: 'number', header: 'Number', render: (p) => p.phone_number_e164 },
      { key: 'seller', header: 'Seller', render: (p) => sellerName(p.seller_id) },
      { key: 'brand', header: 'Brand', render: (p) => p.brand_id ?? '—' },
      {
        key: 'connect',
        header: 'Connect ID',
        render: (p) => p.connect_phone_number_id ?? <span className="muted">unmapped</span>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (p) => <span className={`badge badge--status-${p.status}`}>{p.status}</span>,
      },
      {
        key: 'actions',
        header: '',
        render: (p) => (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (confirm(`Delete number "${p.phone_number_e164}"?`)) deleteMutation.mutate(p.id);
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation, sellersQ.data],
  );

  function set<K extends keyof PhoneForm>(key: K, value: PhoneForm[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  return (
    <div className="page">
      <PageHeader
        title="Phone Numbers"
        subtitle="Map inbound numbers to sellers and Amazon Connect."
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New mapping
          </button>
        }
      />

      <div className="toolbar">
        <select
          className="field__input"
          value={sellerFilter}
          onChange={(e) => setSellerFilter(e.target.value)}
        >
          <option value="">All sellers</option>
          {sellersQ.data?.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {deleteMutation.isError && <div className="form-error">{errorMessage(deleteMutation.error)}</div>}

      <DataTable
        columns={columns}
        rows={numbersQ.data}
        rowKey={(p) => p.id}
        isLoading={numbersQ.isLoading}
        isError={numbersQ.isError}
        error={numbersQ.error}
        onRetry={() => numbersQ.refetch()}
        emptyMessage="No phone numbers mapped yet."
      />

      <Modal open={open} title="New phone number mapping" onClose={() => setOpen(false)}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <label className="field">
            <span className="field__label">Seller</span>
            <select
              className="field__input"
              value={form.seller_id}
              onChange={(e) => set('seller_id', e.target.value)}
              required
            >
              <option value="">Select…</option>
              {sellersQ.data?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Brand (optional)</span>
            <select
              className="field__input"
              value={form.brand_id}
              onChange={(e) => set('brand_id', e.target.value)}
              disabled={!form.seller_id}
            >
              <option value="">None</option>
              {brandsQ.data?.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Phone number (E.164)</span>
            <input
              className="field__input"
              placeholder="+14155550123"
              value={form.phone_number_e164}
              onChange={(e) => set('phone_number_e164', e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Connect phone number ID (optional)</span>
            <input
              className="field__input"
              value={form.connect_phone_number_id}
              onChange={(e) => set('connect_phone_number_id', e.target.value)}
            />
          </label>

          {createMutation.isError && <div className="form-error">{errorMessage(createMutation.error)}</div>}

          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={createMutation.isPending || !form.seller_id || !form.phone_number_e164}
            >
              {createMutation.isPending ? 'Saving…' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
